/**
 * MainMenu — title, version subtitle, Play / Settings / About buttons.
 *
 * The home screen deliberately stays sparse: just the game name and the
 * version directly underneath. The full description lives on the About
 * scene. This keeps the menu uncluttered when sitting on top of a busy
 * pixel-art background.
 *
 * Black text strokes keep titles readable against any background when
 * a per-game theme provides one.
 *
 * Responsive: the layout function is re-run on every `scale.resize`
 * event (debounced), so orientation changes and browser-window resizes
 * reflow the UI to the new dimensions.
 */
import { Theme } from '../ui/Theme.js';
import { Button } from '../ui/Button.js';
import { SceneBackground } from '../ui/SceneBackground.js';
import { onResize, layoutSlot } from './ResponsiveScene.js';

export class MainMenu extends Phaser.Scene {
  constructor() {
    super('MainMenu');
  }

  create() {
    this.cameras.main.setBackgroundColor(Theme.colors.bg);

    const slot = layoutSlot();
    this._slot = slot;

    onResize(this, () => {
      slot.wipe();
      // Re-apply the background at the new dimensions. The page bg
      // helper is idempotent so resize doesn't flicker it.
      const bg = SceneBackground.fromRegistry(this);
      if (bg) slot.add(bg);
      this._layout(slot);
    });
  }

  _layout(slot) {
    const reg = this.registry;
    const manifest = reg.get('manifest');
    const i18n = reg.get('i18n');

    const W = this.scale.width;
    const H = this.scale.height;
    const cx = W / 2;

    // Title at ~18% from top.
    slot.add(this.add.text(cx, H * 0.18, manifest.name, {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.xxl,
      color: Theme.colors.text,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 6
    }).setOrigin(0.5));

    // Version directly underneath the title.
    slot.add(this.add.text(cx, H * 0.30, 'v' + manifest.version, {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.md,
      color: Theme.colors.text,
      fontStyle: 'italic',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5));

    // Button stack: 3 buttons, 60px apart, centered around y=55%.
    const btnGap = 60;
    const stackTop = H * 0.55;
    const playBtn = new Button({
      scene: this,
      x: cx, y: stackTop,
      label: i18n.t('menu.play', 'Play'),
      onClick: () => this.scene.start('Lobby')
    });
    slot.add(playBtn.container);
    const settingsBtn = new Button({
      scene: this,
      x: cx, y: stackTop + btnGap,
      label: i18n.t('menu.settings', 'Settings'),
      variant: 'secondary',
      onClick: () => this.scene.launch('Settings', { from: 'MainMenu' })
    });
    slot.add(settingsBtn.container);
    const aboutBtn = new Button({
      scene: this,
      x: cx, y: stackTop + btnGap * 2,
      label: i18n.t('menu.about', 'About'),
      variant: 'secondary',
      onClick: () => this.scene.start('About')
    });
    slot.add(aboutBtn.container);
  }
}
